/**
 * The app: owns the connection and the stored profile, and decides which
 * screen the page shows. Each screen is a custom element already in the
 * page's markup; this hands them data and answers the events they raise.
 */

import type { GameView } from "@ryzun/haf-saloon-game-engine";
import type { ServerMessage, TableLobbyMessage, TeamPresentation } from "../server/protocol.js";
import { type Connection, type ConnectionStatus, connect } from "./connection.js";
import {
    type StoredFriend,
    type StoredProfile,
    PROFILE_KEY,
    loadFriends,
    loadProfile,
    removeFriend,
    saveFriend,
    saveProfile
} from "./db.js";
import {
    buildInviteUrl,
    diffSeats,
    getSeatHolders,
    isPlaying,
    isTableId,
    readInvite,
    removeInvite,
    shouldShowLobby
} from "./lobby.js";
import { type LoggedEvent, appendEvent, describeHighlights, startRound } from "./log.js";
import { describeConnection, getRetryDelay, loadResumeToken, saveResumeToken } from "./reconnect.js";

/** Shown when the server no longer has the game the player was coming back to. */
const EXPIRED_NOTICE = "Your game couldn't be picked back up. It ended while you were away.";

/**
 * Shown for an invite that cannot be a table at all. Worded as the server words
 * a table that has closed, since to the player the two are the same dead link.
 */
const DEAD_LINK_NOTICE = "That table has closed, or the link is incomplete.";

/** Every new table starts at the smaller size; the host can change it while seating. */
const DEFAULT_PLAYER_COUNT = 4;

export interface AppElements {
    readonly banner: HTMLElement;
    readonly bannerMessage: HTMLElement;
    readonly bannerAction: HTMLButtonElement;
    readonly loading: HTMLElement;
    readonly landing: HTMLElementTagNameMap["saloon-landing"];
    readonly table: HTMLElementTagNameMap["game-table"];
    readonly profileDialog: HTMLElementTagNameMap["profile-dialog"];
    readonly lobby: HTMLElementTagNameMap["table-lobby"];
}

export class App {
    readonly #elements: AppElements;

    #connection: Connection | null = null;

    #status: ConnectionStatus = "connecting";

    /** True from a dropped connection until one opens again. */
    #lost = false;

    /** How many automatic attempts have run since the connection was last open. */
    #attempt = 0;

    #retryTimer: ReturnType<typeof setTimeout> | null = null;

    /** Proves the seat at a game the server is holding; null when there is none. */
    #resumeToken: string | null = loadResumeToken();

    #notice: string | null = null;

    #profile: StoredProfile | null = null;

    /** True until a profile has been saved, which is what makes the dialog modal. */
    #firstVisit = true;

    /**
     * True from a first visit until the profile dialog is completed. An invite
     * waits on it, so a newcomer arrives at the table under the name they chose.
     */
    #settingUp = false;

    /** The table the page was opened to join, until the request to join it is sent. */
    #invite: string | null = readInvite(window.location.search);

    /** Who sits where at the current table, or null when not at one. */
    #lobby: TableLobbyMessage | null = null;

    /** When the game at this table began, by this page's clock; null before the deal. */
    #startedAt: number | null = null;

    #candidateName = "";

    /** True until the first name request after the profile dialog opens, which starts a new cycle. */
    #restartNames = true;

    #view: GameView | null = null;

    #teamColors: readonly TeamPresentation[] = [];

    #events: readonly LoggedEvent[] = [];

    /** The latest action in full, for screen readers; the feed shows only some. */
    #announcement = "";

    #friends: readonly StoredFriend[] = [];

    constructor(elements: AppElements) {
        this.#elements = elements;
        this.#listen();
    }

    async start(): Promise<void> {
        const stored = await loadProfile();

        this.#firstVisit = stored === null;
        this.#settingUp = stored === null;
        this.#profile = stored;

        // Following an invite means going to that table, not back to one held
        // from earlier in this tab.
        if (this.#invite !== null) {
            this.#setResumeToken(null);
        }
        this.#candidateName = stored?.name ?? "";
        this.#friends = await loadFriends();
        this.#update();

        this.#connection = connect({
            onMessage: (message) => this.#onMessage(message),
            onStatus: (status) => this.#onStatus(status)
        });
    }

    #listen(): void {
        const { bannerAction, landing, table, profileDialog, lobby } = this.#elements;

        bannerAction.addEventListener("click", () => {
            if (this.#status === "open") {
                this.#notice = null;
                this.#update();
                return;
            }

            this.#reconnectNow();
        });

        landing.addEventListener("play-requested", () => {
            if (this.#profile !== null) {
                this.#clearTable();
                this.#connection?.send({ type: "create-table", playerCount: DEFAULT_PLAYER_COUNT });
            }
        });

        landing.addEventListener("edit-profile", () => this.#openProfile(null));
        landing.addEventListener("friend-add", (event) => void this.#addFriend(event.detail.name));
        landing.addEventListener("friend-accept", (event) => void this.#acceptFriend(event.detail.id));
        landing.addEventListener("friend-decline", (event) => void this.#dropFriend(event.detail.id));
        landing.addEventListener("friend-remove", (event) => void this.#dropFriend(event.detail.id));

        profileDialog.addEventListener("names-requested", () => {
            this.#connection?.send({ type: "request-names", count: 1, restart: this.#restartNames });
            this.#restartNames = false;
        });

        // The server has the last word on the name, so this is a request rather
        // than a commit; its reply is what gets persisted.
        profileDialog.addEventListener("profile-save", (event) => {
            const choice = event.detail;

            if (this.#profile !== null) {
                this.#profile = { ...this.#profile, fourColorDeck: choice.fourColorDeck, color: choice.color };
            }

            // The server's reply to this is what sends a pending invite on.
            this.#settingUp = false;
            this.#connection?.send({ type: "set-profile", profile: { name: choice.name, color: choice.color } });
            this.#update();
        });

        profileDialog.addEventListener("profile-cancel", () => {
            this.#candidateName = this.#profile?.name ?? this.#candidateName;
        });

        lobby.addEventListener("seat-take", (event) => {
            this.#connection?.send({ type: "take-seat", seatIndex: event.detail.seatIndex });
        });

        lobby.addEventListener("player-count-change", (event) => {
            this.#connection?.send({ type: "set-player-count", playerCount: event.detail.playerCount });
        });

        lobby.addEventListener("team-color-change", (event) => {
            this.#connection?.send({ type: "set-team-color", color: event.detail.color });
        });

        lobby.addEventListener("game-start", () => this.#connection?.send({ type: "start-game" }));
        lobby.addEventListener("leave-table", () => this.#connection?.send({ type: "leave-game" }));

        table.addEventListener("game-action", (event) => {
            this.#connection?.send({ type: "game-action", action: event.detail.action });
        });

        table.addEventListener("leave-table", () => this.#connection?.send({ type: "leave-game" }));
    }

    /** Puts away everything left from the last table, ready for the next. */
    #clearTable(): void {
        this.#notice = null;
        this.#events = [];
        this.#announcement = "";
        this.#elements.table.reset();
    }

    /** Sends a pending invite once the player has a name the server agreed to. */
    #joinInvite(): void {
        const tableId = this.#invite;

        if (tableId === null || this.#settingUp || this.#profile === null) {
            return;
        }

        this.#invite = null;
        this.#clearTable();

        // A reload from here should resume this seat with the token the join
        // hands out - or, for a dead link, simply show the saloon - not follow
        // the invite in all over again.
        window.history.replaceState(window.history.state, "", removeInvite(window.location.href));

        if (!isTableId(tableId)) {
            this.#notice = DEAD_LINK_NOTICE;
            this.#update();
            return;
        }

        this.#connection?.send({ type: "join-table", tableId });
    }

    #persist(next: StoredProfile): void {
        this.#profile = next;
        this.#candidateName = next.name;
        void saveProfile({ name: next.name, color: next.color, fourColorDeck: next.fourColorDeck });
    }

    #openProfile(notice: string | null): void {
        const profile = this.#profile;

        if (profile === null) {
            return;
        }

        this.#restartNames = true;
        this.#elements.profileDialog.show({
            name: this.#candidateName,
            color: profile.color,
            fourColorDeck: profile.fourColorDeck,
            dismissable: !this.#firstVisit,
            notice
        });
    }

    #onMessage(message: ServerMessage): void {
        switch (message.type) {
            case "profile": {
                this.#persist({
                    id: PROFILE_KEY,
                    name: message.profile.name,
                    color: message.profile.color,
                    fourColorDeck: this.#profile?.fourColorDeck ?? false
                });

                this.#elements.profileDialog.name = message.profile.name;
                this.#elements.profileDialog.notice = message.substituted ? message.reason : null;

                if (this.#firstVisit) {
                    this.#firstVisit = false;
                    this.#openProfile(message.substituted ? message.reason : null);
                }

                this.#joinInvite();
                break;
            }

            case "table-lobby": {
                // Sitting down at a game under way starts the table afresh; a
                // resend of the same seating after a reconnect leaves it be.
                if (!isPlaying(this.#lobby) && isPlaying(message)) {
                    this.#clearTable();
                }

                this.#elements.table.noteSeatChanges(diffSeats(this.#lobby, message));
                this.#lobby = message;

                // The server says how long ago rather than when, so the two
                // clocks never have to agree.
                this.#startedAt = message.elapsedMs === null ? null : Date.now() - message.elapsedMs;
                break;
            }

            case "join-failed": {
                this.#leaveTable();
                this.#notice = message.reason;
                break;
            }

            case "name-suggestions": {
                this.#candidateName = message.names[0] ?? this.#candidateName;
                this.#elements.profileDialog.name = this.#candidateName;
                break;
            }

            case "game-state": {
                // Read by the table when the update below hands it this view.
                if (message.dealMs !== null) {
                    this.#elements.table.expectDeal(message.dealMs);
                }

                if (message.tallyMs !== null) {
                    this.#elements.table.expectTally(message.tallyMs);
                }

                this.#view = message.view;
                this.#teamColors = message.teamColors;
                this.#events = startRound(this.#events, message.view.round, message.view.roundMinimum);

                if (message.event !== null) {
                    const line = describeHighlights(message.event);

                    this.#announcement = `${message.event.playerName} ${message.event.text}`;

                    if (line !== null) {
                        this.#events = appendEvent(this.#events, { text: line, automated: message.event.automated });
                    }
                }

                break;
            }

            // The feed only carries table moments now, so a refusal is shown
            // over the felt where the player is already looking.
            case "action-rejected": {
                this.#elements.table.reject(message.message);
                break;
            }

            case "error": {
                if (shouldShowLobby(this.#lobby)) {
                    this.#elements.lobby.notify(message.message);
                }
                else {
                    this.#elements.table.notify(message.message);
                }

                break;
            }

            case "resume-token": {
                this.#setResumeToken(message.token);
                break;
            }

            case "resume-failed": {
                this.#setResumeToken(null);
                this.#notice = EXPIRED_NOTICE;
                this.#leaveTable();
                break;
            }

            case "game-ended": {
                this.#setResumeToken(null);
                this.#leaveTable();
                break;
            }
        }

        this.#update();
    }

    #leaveTable(): void {
        this.#lobby = null;
        this.#startedAt = null;
        this.#view = null;
        this.#teamColors = [];
        this.#events = [];
        this.#announcement = "";
    }

    #setResumeToken(token: string | null): void {
        this.#resumeToken = token;
        saveResumeToken(token);
    }

    #onStatus(status: ConnectionStatus): void {
        this.#status = status;

        if (status === "open") {
            const profile = this.#profile;

            this.#lost = false;
            this.#attempt = 0;
            this.#connection?.send({
                type: "hello",
                profile: profile === null ? null : { name: profile.name, color: profile.color }
            });

            if (this.#resumeToken !== null) {
                this.#connection?.send({ type: "resume-game", token: this.#resumeToken });
            }
        }

        // A replaced socket is not retried: the other tab would only take the
        // game straight back, and the two would trade it forever.
        if (status === "closed" || status === "replaced") {
            this.#lost = true;
        }

        if (status === "closed") {
            this.#scheduleRetry();
        }

        this.#update();
    }

    /** Queues the next automatic attempt, unless one is queued or the attempts have run out. */
    #scheduleRetry(): void {
        const delay = getRetryDelay(this.#attempt);

        if (this.#retryTimer !== null || delay === null) {
            return;
        }

        this.#retryTimer = setTimeout(() => {
            this.#retryTimer = null;
            this.#attempt += 1;
            this.#connection?.reconnect();
        }, delay);
    }

    /** The player asked: skip the wait, and start the automatic attempts over. */
    #reconnectNow(): void {
        if (this.#retryTimer !== null) {
            clearTimeout(this.#retryTimer);
            this.#retryTimer = null;
        }

        this.#attempt = 0;
        this.#connection?.reconnect();
    }

    async #addFriend(name: string): Promise<void> {
        await saveFriend({
            id: `friend-${Date.now().toString(36)}`,
            name,
            status: "offline",
            relationship: "outgoing"
        });

        this.#friends = await loadFriends();
        this.#update();
    }

    async #acceptFriend(id: string): Promise<void> {
        const existing = this.#friends.find((friend) => friend.id === id);

        if (existing !== undefined) {
            await saveFriend({ ...existing, relationship: "friend" });
            this.#friends = await loadFriends();
            this.#update();
        }
    }

    async #dropFriend(id: string): Promise<void> {
        await removeFriend(id);
        this.#friends = await loadFriends();
        this.#update();
    }

    /** Shows the screen the current state calls for and hands it its data. */
    #update(): void {
        const { banner, bannerMessage, bannerAction, loading, landing, table, lobby } = this.#elements;
        const profile = this.#profile;
        const atTable = this.#view !== null || this.#lobby !== null;
        const notice = describeConnection({
            status: this.#status,
            lost: this.#lost,
            retrying: this.#retryTimer !== null,
            holdingGame: this.#resumeToken !== null,
            notice: this.#notice
        });

        // The four-color deck is a document-level switch the card styles key off.
        document.documentElement.dataset.deck = profile?.fourColorDeck === true ? "four-color" : "standard";

        // Shown at the table too: a dropped connection is the one thing a player
        // must not miss, and the activity feed no longer reports it.
        banner.hidden = notice === null;

        if (notice !== null && bannerMessage.textContent !== notice.message) {
            bannerMessage.textContent = notice.message;
        }

        bannerAction.textContent = notice?.action ?? "";
        bannerAction.disabled = notice?.actionDisabled ?? false;
        loading.hidden = profile !== null;
        landing.hidden = profile === null || atTable;
        table.hidden = profile === null || !atTable;

        landing.friends = this.#friends;

        lobby.model = profile !== null && this.#lobby !== null && shouldShowLobby(this.#lobby)
            ? { lobby: this.#lobby, inviteUrl: buildInviteUrl(window.location, this.#lobby.tableId) }
            : null;

        table.invite = this.#lobby === null
            ? null
            : { tableId: this.#lobby.tableId, url: buildInviteUrl(window.location, this.#lobby.tableId) };
        table.startedAt = this.#startedAt;
        table.teamColors = this.#teamColors;
        table.holders = getSeatHolders(this.#lobby);
        table.events = this.#events;
        table.announcement = this.#announcement;
        table.view = this.#view;
    }
}
